import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import {
  Assessment as DashboardIcon,
  GetApp as DownloadIcon,
  DateRange as DateIcon,
  Store as StoreIcon,
  FilterList as FilterIcon,
  PlayArrow as GenerateIcon,
  Schedule as SlotIcon,
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';

const todayStr = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const OrderStatusDashboard = () => {
  const { actions } = useApp();

  const [filters, setFilters] = useState({
    startDate: todayStr(),
    endDate: todayStr(),
    storeId: 'all',
    slotTiming: 'all',
  });

  const [stores, setStores] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const storesData = await cacheEndpoints.stores.getAll();
        setStores(storesData || []);
      } catch (e) {
        console.error('Error fetching stores:', e);
      }
      try {
        const res = await api.get('/time-slots');
        const slots = res.data?.timeSlots || res.data?.slots || [];
        const labels = [
          ...new Set(
            slots
              .map((s) => (s.startTime && s.endTime ? `${s.startTime} - ${s.endTime}` : s.displayTime || null))
              .filter(Boolean)
          ),
        ].sort();
        setAvailableSlots(labels);
      } catch (e) {
        console.error('Error fetching time slots:', e);
      }
    };
    init();
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const buildParams = (format) => {
    const params = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      format,
    };
    if (filters.storeId !== 'all') params.storeId = filters.storeId;
    if (filters.slotTiming !== 'all') params.slotTiming = filters.slotTiming;
    return params;
  };

  const isRangeValid = () => {
    if (!filters.startDate || !filters.endDate) return false;
    return new Date(filters.startDate) <= new Date(filters.endDate);
  };

  const generateReport = async () => {
    if (!isRangeValid()) {
      setError('Please select a valid date range.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setReport(null);
      const res = await api.get('/reports/order-status-dashboard', {
        params: buildParams('json'),
      });
      if (res.data?.success) {
        setReport(res.data);
        setAvailableSlots(res.data.availableSlots || []);
        if ((res.data.rows || []).length === 0) {
          actions.showSnackbar('No orders found for the selected filters', 'info');
        }
      } else {
        setError(res.data?.message || 'Failed to generate dashboard');
      }
    } catch (e) {
      console.error('Error generating order status dashboard:', e);
      setError(e.response?.data?.message || 'Error generating dashboard');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!isRangeValid()) {
      setError('Please select a valid date range.');
      return;
    }
    try {
      setDownloading(true);
      setError('');
      const res = await api.get('/reports/order-status-dashboard', {
        params: buildParams('xlsx'),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `order_status_dashboard_${filters.startDate}_to_${filters.endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      actions.showSnackbar('Dashboard exported successfully!', 'success');
    } catch (e) {
      console.error('Error downloading order status dashboard:', e);
      setError('Error downloading dashboard');
      actions.showSnackbar('Error downloading dashboard', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const totals = report?.totals;
  const summaryCards = [
    { label: 'Total Orders', value: totals?.totalOrders ?? 0 },
    { label: 'Pending', value: totals?.pending ?? 0 },
    { label: 'Confirmed', value: totals?.confirmed ?? 0 },
    { label: 'Processing', value: totals?.processing ?? 0 },
    { label: 'Assigned + OFD', value: totals?.assignedOutForDelivery ?? 0 },
    { label: 'Delivered', value: totals?.delivered ?? 0 },
    { label: 'Cancelled', value: totals?.cancelled ?? 0 },
    { label: 'Returned', value: totals?.returned ?? 0 },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <DashboardIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            Order Status Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Live order counts by branch and slot timing, filtered by delivery date (not order placement date).
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1 }} /> Filters
        </Typography>
        <Divider sx={{ my: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Delivery From Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayStr() }}
              fullWidth
              InputProps={{ startAdornment: <DateIcon color="action" sx={{ mr: 1 }} /> }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Delivery To Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayStr() }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Store</InputLabel>
              <Select
                value={filters.storeId}
                onChange={(e) => handleFilterChange('storeId', e.target.value)}
                label="Store"
                startAdornment={<StoreIcon color="action" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.branchId || store.id}>
                    {store.storeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Slot Timing</InputLabel>
              <Select
                value={filters.slotTiming}
                onChange={(e) => handleFilterChange('slotTiming', e.target.value)}
                label="Slot Timing"
                startAdornment={<SlotIcon color="action" sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">All Slots</MenuItem>
                {availableSlots.map((slot) => (
                  <MenuItem key={slot} value={slot}>
                    {slot}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={generateReport}
            disabled={loading || !isRangeValid()}
            startIcon={loading ? <CircularProgress size={20} /> : <GenerateIcon />}
            sx={{ minWidth: 200 }}
          >
            {loading ? 'Generating...' : 'Generate Dashboard'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={downloadReport}
            disabled={downloading || !isRangeValid()}
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            sx={{ minWidth: 200 }}
          >
            {downloading ? 'Preparing...' : 'Download Excel'}
          </Button>
        </Box>
      </Paper>

      {report && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {summaryCards.map((card) => (
              <Grid item xs={6} sm={4} md={3} key={card.label}>
                <Card sx={{ textAlign: 'center' }}>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h5" color="primary.main">
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper>
            <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                Results ({report.rows.length} rows)
              </Typography>
              <Chip label={`${filters.startDate} → ${filters.endDate}`} size="small" />
              {filters.storeId !== 'all' && <Chip label={`Store: ${filters.storeId}`} size="small" />}
              {filters.slotTiming !== 'all' && <Chip label={`Slot: ${filters.slotTiming}`} size="small" />}
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Branch Name</TableCell>
                    <TableCell>Slot Timing</TableCell>
                    <TableCell align="right">Total Orders</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="right">Confirmed</TableCell>
                    <TableCell align="right">Processing</TableCell>
                    <TableCell align="right">Assigned + Out For Delivery</TableCell>
                    <TableCell align="right">Delivered</TableCell>
                    <TableCell align="right">Cancelled</TableCell>
                    <TableCell align="right">Returned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((r, i) => (
                    <TableRow key={`${r.branchName}-${r.slotTiming}-${i}`} hover>
                      <TableCell>{r.branchName}</TableCell>
                      <TableCell>{r.slotTiming}</TableCell>
                      <TableCell align="right"><strong>{r.totalOrders}</strong></TableCell>
                      <TableCell align="right">{r.pending}</TableCell>
                      <TableCell align="right">{r.confirmed}</TableCell>
                      <TableCell align="right">{r.processing}</TableCell>
                      <TableCell align="right">{r.assignedOutForDelivery}</TableCell>
                      <TableCell align="right">{r.delivered}</TableCell>
                      <TableCell align="right">{r.cancelled}</TableCell>
                      <TableCell align="right">{r.returned}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default OrderStatusDashboard;
